/**
 * Input guards — what a field will *accept* as it is typed or pasted, as opposed to what it
 * reports as invalid afterwards.
 *
 * <p>The two layers are separate on purpose. A guard silently shapes the value (drop a letter from
 * a digits-only field, cap the length); a rule explains a problem the user has to fix. Using a
 * guard where a rule belongs is how a field appears to swallow input for no stated reason — so
 * guards are limited to the cases where the correction is obvious and self-evident.</p>
 *
 * <p>Every guard runs on the input's `value`, which means a paste goes through exactly the same
 * path as a keystroke. There is no separate onPaste handler to fall out of step — the requirement
 * that pasted values are validated like typed ones is satisfied structurally.</p>
 */

/** Trim and collapse runs of whitespace. Mirrors `Sanitize.Text` on the server. */
export function cleanText(value: string): string {
  return (value ?? '').trim().replace(/\s{2,}/g, ' ');
}

/** Digits only. Mirrors `Sanitize.Digits` on the server. */
export function onlyDigits(value: string): string {
  return (value ?? '').replace(/\D/g, '');
}

/**
 * Digits plus a single decimal point, capped to `places` decimals.
 *
 * Set `signed` where a negative is meaningful — an opening balance is a credit balance when
 * negative, and stock goes negative when an issue is posted before its matching receipt.
 * Filtering the minus out would make those untypeable while the rule still claimed to allow them.
 */
export function onlyDecimal(value: string, places = 2, signed = false): string {
  const raw = value ?? '';
  const negative = signed && raw.trimStart().startsWith('-');
  let s = raw.replace(/[^\d.]/g, '');
  const first = s.indexOf('.');
  if (first !== -1) {
    // Keep the first point, drop any others — "1.2.3" is a paste artefact, not an intent.
    s = s.slice(0, first + 1) + s.slice(first + 1).replace(/\./g, '');
    const [whole, frac = ''] = s.split('.');
    s = `${whole}.${frac.slice(0, places)}`;
  }
  return negative ? `-${s}` : s;
}

/** Collapse consecutive spaces as they are typed, but keep a single trailing space so the user can
 *  still type the next word. Leading spaces are dropped outright. */
export function collapseSpaces(value: string): string {
  return (value ?? '').replace(/^\s+/, '').replace(/\s{2,}/g, ' ');
}

/** Uppercase + strip anything outside A-Z0-9 — GSTIN, PAN, IFSC, vehicle numbers. */
export function upperAlnum(value: string): string {
  return (value ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/** A document/reference code: letters, digits, dash, slash, underscore. */
export function onlyCode(value: string): string {
  return (value ?? '').replace(/[^A-Za-z0-9\-/_]/g, '');
}

/**
 * The guard applied to ordinary free text.
 *
 * Strips the characters that carry an injection payload and can't appear in a legitimate name or
 * description — angle brackets and the SQL comment/terminator marks. Quotes and apostrophes are
 * deliberately KEPT: O'Brien is a real name, and the server parameterises its queries, so removing
 * them would break real input to defend against a threat that isn't there.
 */
export function guardText(value: string): string {
  return (value ?? '').replace(/[<>]/g, '').replace(/--|;|\/\*|\*\//g, '');
}

/** dd-MM-yyyy mask: keep digits, re-insert dashes, hard-cap at 10 characters. */
export function maskDate(value: string): string {
  const d = onlyDigits(value).slice(0, 8);
  return [d.slice(0, 2), d.slice(2, 4), d.slice(4, 8)].filter(Boolean).join('-');
}

/** HH:mm mask: keep digits, insert the colon, hard-cap at 5 characters. */
export function maskTime(value: string): string {
  const d = onlyDigits(value).slice(0, 4);
  return d.length <= 2 ? d : `${d.slice(0, 2)}:${d.slice(2)}`;
}

/** The guard modes a field can declare. */
export type GuardMode =
  | 'text'
  | 'digits'
  | 'decimal'
  /** A decimal that may be negative — stock on-hand, an opening balance. */
  | 'decimalSigned'
  | 'money'
  | 'name'
  | 'code'
  | 'upper'
  | 'date'
  | 'time'
  | 'none';

/**
 * Apply a guard plus a hard character cap.
 *
 * The cap is enforced here rather than only via `maxLength` on the element, because `maxLength` is
 * not applied to a programmatic value and is inconsistently applied to a paste on some engines.
 */
export function applyGuard(value: string, mode: GuardMode = 'text', maxLen?: number): string {
  let out = value ?? '';
  switch (mode) {
    case 'digits':
      out = onlyDigits(out);
      break;
    case 'decimal':
      out = onlyDecimal(out, 3);
      break;
    case 'decimalSigned':
      out = onlyDecimal(out, 3, true);
      break;
    case 'money':
      out = onlyDecimal(out, 2);
      break;
    case 'name':
      out = collapseSpaces(guardText(out));
      break;
    case 'code':
      out = onlyCode(out);
      break;
    case 'upper':
      out = upperAlnum(out);
      break;
    case 'date':
      out = maskDate(out);
      break;
    case 'time':
      out = maskTime(out);
      break;
    case 'text':
      out = collapseSpaces(guardText(out));
      break;
    case 'none':
    default:
      break;
  }
  if (maxLen != null && out.length > maxLen) out = out.slice(0, maxLen);
  return out;
}
